import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { History, MoreVertical, Pencil, Undo2 } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import Field from '@/components/ui/Field'
import IconButton from '@/components/ui/IconButton'
import { MenuActionItem } from '@/components/ui/MenuItem'
import Modal from '@/components/ui/Modal'
import Popover, { type PopoverTriggerProps } from '@/components/ui/Popover'
import SectionHead from '@/components/ui/SectionHead'
import TitleRow from '@/components/ui/TitleRow'
import { toast } from '@/components/ui/toast-store'
import { dashboardKeys } from '@/features/dashboard/api/keys'
import { useStudioHandoff } from '@/features/dashboard/handoff'
import {
  useMoveWidget,
  useRemoveBoard,
  useRestoreBoard,
  useSaveBoard,
  useVocabulary,
} from '@/features/dashboard/api/queries'
import { nounOf } from '@/features/dashboard/lib/format'
import { purposeShown } from '@/features/dashboard/lib/signal'
import { applyFix, draftOf } from '@/features/dashboard/lib/formula'
import { isWindowed, presetWidget } from '@/features/dashboard/lib/time'
import {
  MAX_WIDGETS,
  type DashboardBody as Body,
  type Drawn,
  type Fix,
  type BoardSummary,
  type Preset,
  type SaveResult,
  type ScopeRows,
  type TimeVocabulary,
  type WidgetDraft,
} from '@/features/mitra/api/dashboard'
import { cancelProposal } from '@/features/mitra/api/actions'
import { formatDate, formatTime } from '@/lib/format'
import { AskMitra, Starters, Suggestions } from '@/screens/dashboards/components/Gallery'
import MeasureBuilder from '@/screens/dashboards/components/MeasureBuilder'
import { useArrivedMeasure } from '@/screens/dashboards/useArrivedMeasure'
import {
  BarsCard,
  FigureCard,
  MitraDot,
  ScopeTable,
} from '@/screens/dashboards/components/WidgetCards'
import { WindowCard } from '@/screens/dashboards/components/TimeCards'
import { addToDashboard, inDashboard } from '@/features/mitra/lib/pagePrefix'
import WidgetEditor from '@/screens/dashboards/components/WidgetEditor'
import { OpenExisting, useNameCheck } from '@/screens/dashboards/components/NameDialog'

/**
 * One of the reader's dashboards: its name (and its purpose, where that adds
 * to the name), its measures under their headings, worked out now.
 *
 * NOTHING HERE COMPUTES. Every value is the engine's, from the formula the
 * reader (or Mitra, on their word) chose; this lays the widgets out by
 * section and by what they are over, lets the reader add one without a
 * formula, edit one with, and saves the definitions back.
 *
 * A SAVE IS THE WHOLE LIST, CONDITIONAL ON THE VERSION SHOWN. Two open tabs
 * cannot quietly undo each other: the engine answers a stale save with a
 * conflict, and the reader reloads rather than losing a widget.
 *
 * EVERY CHANGE IS ONE TAP FROM UNDONE. Removing, moving and applying a fix each
 * raise a toast with Undo, which saves the list as it was. And the engine
 * keeps the last five versions of the dashboard: Versions puts any of them
 * back, and "Undo Mitra's change" is that for the newest when Mitra made it.
 */

type Editing = {
  at: number | 'new'
  draft: WidgetDraft
  /** Handed over from a chat proposal to edit before it is saved. */
  handed?: boolean
}

/** The engine's refusal as the screen keeps it: the sentence, and what it acts on. */
type Refusal = { message: string; code: string | null; existing: string | null }

function Menu({
  label,
  trigger,
  children,
}: {
  label: string
  trigger: (props: PopoverTriggerProps) => ReactNode
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      hasPopup="menu"
      label={label}
      className="w-60"
      trigger={trigger}
    >
      {children(() => {
        setOpen(false)
      })}
    </Popover>
  )
}

/** A widget's other actions, behind one ⋯ button. */
function ActionsMenu({
  title,
  disabled,
  onEdit,
  onMove,
  onRemove,
  others,
  onMoveTo,
}: {
  title: string
  disabled: boolean
  onEdit: () => void
  onMove: (to: 'up' | 'down' | 'top') => void
  onRemove: () => void
  /** The reader's other dashboards, each a place this widget can go. */
  others: readonly BoardSummary[]
  onMoveTo: (board: BoardSummary) => void
}) {
  return (
    <Menu
      label={`Actions for ${title}`}
      trigger={(props) => (
        <IconButton {...props} aria-label={`More actions for ${title}`} className="size-8 shrink-0">
          <MoreVertical className="size-4" aria-hidden="true" />
        </IconButton>
      )}
    >
      {(close) => {
        const item = (label: string, act: () => void, danger = false) => (
          <MenuActionItem
            label={label}
            danger={danger}
            disabled={disabled}
            tabIndex={-1}
            onClick={() => {
              close()
              act()
            }}
          />
        )
        return (
          <>
            {item('Edit', onEdit)}
            {item('Move up', () => {
              onMove('up')
            })}
            {item('Move down', () => {
              onMove('down')
            })}
            {item('Move to top', () => {
              onMove('top')
            })}
            {others.map((board) => (
              <MenuActionItem
                key={board.id}
                label={`Move to “${board.name}”`}
                disabled={disabled}
                tabIndex={-1}
                onClick={() => {
                  close()
                  onMoveTo(board)
                }}
              />
            ))}
            {item('Remove', onRemove, true)}
          </>
        )
      }}
    </Menu>
  )
}

/** When a kept version was left, in Kathmandu. */
const whenOf = (at: string): string => `${formatDate(at)} · ${formatTime(at)}`

/** The dashboard's name and purpose, edited in place. */
function RenameForm({
  name,
  purpose,
  saving,
  boards,
  boardId,
  refusal,
  onSave,
  onCancel,
}: {
  name: string
  purpose: string
  saving: boolean
  /** The reader's dashboards: a name one of them has is said as it is typed. */
  boards: readonly BoardSummary[]
  boardId: string
  /** The engine's refusal of the name, with the dashboard that has it. */
  refusal: Refusal | null
  onSave: (name: string, purpose: string) => void
  onCancel: () => void
}) {
  const [nextName, setNextName] = useState(name)
  const [nextPurpose, setNextPurpose] = useState(purpose)
  const { clash, message } = useNameCheck(boards, nextName, boardId)
  const shownRefusal = refusal?.code === 'name_taken' ? refusal : null
  const existing = clash?.id ?? shownRefusal?.existing ?? null
  return (
    <form
      data-role="rename-form"
      onSubmit={(event) => {
        event.preventDefault()
        if (nextName.trim() && !clash) onSave(nextName.trim(), nextPurpose.trim())
      }}
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-card p-4"
    >
      <Field
        label="Name"
        value={nextName}
        maxLength={60}
        autoFocus
        error={message ?? shownRefusal?.message}
        onChange={(e) => {
          setNextName(e.target.value)
        }}
      />
      {existing ? (
        <div>
          <OpenExisting id={existing} />
        </div>
      ) : null}
      <Field
        label="What it is for"
        value={nextPurpose}
        maxLength={160}
        onChange={(e) => {
          setNextPurpose(e.target.value)
        }}
      />
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={saving || !nextName.trim() || clash !== null}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

/** The widgets of one heading, laid out: figures, a table per scope, bars. */
function Section({
  title,
  widgets,
  scopes,
  holdings,
  actionsFor,
  onApplyFix,
  time,
}: {
  title: string | null
  widgets: Drawn[]
  scopes: readonly ScopeRows[]
  holdings: string[]
  actionsFor: (widget: Drawn) => ReactNode
  onApplyFix: (widget: Drawn, fix: Fix) => void
  time: TimeVocabulary | null
}) {
  const scopeOf = (widget: Drawn): ScopeRows | undefined =>
    scopes.find((s) => s.key === widget.scopeKey)
  // A widget over a window or over brokers draws on its own card, whatever
  // its kind: a series is not a column of today's holdings table.
  const windowed = widgets.filter(isWindowed)
  const today = widgets.filter((w) => !isWindowed(w))
  const figures = today.filter((w) => w.kind === 'number')
  const bars = today.filter((w) => w.kind === 'bars' || w.kind === 'sector_bars')
  const tables = new Map<string, Drawn[]>()
  for (const widget of today.filter((w) => w.kind === 'column')) {
    const key = widget.scopeKey ?? 'book'
    tables.set(key, [...(tables.get(key) ?? []), widget])
  }
  return (
    <section
      data-role="dash-section"
      aria-label={title ?? undefined}
      className="flex flex-col gap-4"
    >
      {/* The heading alone: a count of the cards right under it says nothing.
          Inset by the cards' own padding, so its first letter stands on the
          same left edge as every card title under it. */}
      {title ? (
        <SectionHead
          title={title}
          size="card"
          className="px-(--section-card-pad-x) [--section-card-pad-x:var(--space-5)] @max-sm:[--section-card-pad-x:var(--space-4)]"
        />
      ) : null}
      {figures.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 @md:grid-cols-2 @3xl:grid-cols-3">
          {figures.map((widget) => (
            <FigureCard
              key={widget.id ?? widget.title}
              widget={widget}
              noun={nounOf(scopeOf(widget)?.kind)}
              actions={actionsFor(widget)}
              onApplyFix={(fix) => {
                onApplyFix(widget, fix)
              }}
            />
          ))}
        </div>
      ) : null}
      {[...tables].map(([key, columns]) => {
        const scope = scopes.find((s) => s.key === key) ?? {
          key,
          kind: 'book' as const,
          label: 'My holdings',
          rows: holdings,
        }
        return (
          <ScopeTable
            key={key}
            columns={columns}
            scope={scope}
            actions={actionsFor}
            onApplyFix={onApplyFix}
          />
        )
      })}
      {windowed.length > 0 ? (
        <div data-role="window-grid" className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
          {windowed.map((widget) => (
            <WindowCard
              key={widget.id ?? widget.title}
              widget={widget}
              vocabulary={time}
              actions={actionsFor(widget)}
              onApplyFix={(fix) => {
                onApplyFix(widget, fix)
              }}
            />
          ))}
        </div>
      ) : null}
      {bars.map((widget) => (
        <BarsCard
          key={widget.id ?? widget.title}
          widget={widget}
          noun={nounOf(scopeOf(widget)?.kind)}
          actions={actionsFor(widget)}
          onApplyFix={(fix) => {
            onApplyFix(widget, fix)
          }}
        />
      ))}
    </section>
  )
}

/** The headings in their order, each with its widgets; ungrouped ones first. */
function sectionsOf(body: Body): { title: string | null; widgets: Drawn[] }[] {
  const order = [...(body.dashboard?.sections ?? [])]
  for (const widget of body.widgets) if (!order.includes(widget.section)) order.push(widget.section)
  return order
    .map((title) => ({ title, widgets: body.widgets.filter((w) => w.section === title) }))
    .filter((s) => s.widgets.length > 0)
}

export default function DashboardBody({
  body,
  boardId,
  onReload,
  onAskMitra,
}: {
  body: Body
  /** The dashboard's id in the URL. */
  boardId: string
  onReload: () => void
  /** Ask Mitra in the chat, where it builds the measure; null where there is no chat. */
  onAskMitra: ((question: string) => void) | null
}) {
  const widgets = body.widgets
  const empty = widgets.length === 0
  const name = body.dashboard?.name ?? 'My dashboard'
  const purpose = body.dashboard?.purpose ?? ''
  const newest = body.history[0]
  const [editing, setEditing] = useState<Editing | null>(null)
  const [adding, setAdding] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [conflict, setConflict] = useState(false)
  const [refusal, setRefusal] = useState<Refusal | null>(null)
  const refused = refusal?.message ?? null
  const setRefused = (message: string | null) => {
    setRefusal(message === null ? null : { message, code: null, existing: null })
  }
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const saveMutation = useSaveBoard(boardId)
  const restoreMutation = useRestoreBoard(boardId)
  const removeMutation = useRemoveBoard()
  const moveMutation = useMoveWidget(boardId)
  const saving = saveMutation.isPending || restoreMutation.isPending || moveMutation.isPending
  const vocabulary = useVocabulary().data ?? null
  // The purpose under the name only where it adds to it: never the template's
  // stock sentence the engine copied in, never the name again.
  const shownPurpose = body.dashboard
    ? purposeShown(body.dashboard, vocabulary ? vocabulary.templates : null)
    : null
  // Opened from Mitra at the measure a save added: bring it into view.
  useArrivedMeasure(!empty)

  const drafts = (): WidgetDraft[] => widgets.map(draftOf)
  const latestEtag = () =>
    queryClient.getQueryData<Body>(dashboardKeys.board(boardId))?.etag ?? body.etag

  /** What a save or a restore came to, said where the reader is working. */
  const settle = (result: SaveResult): boolean => {
    if (result.kind === 'saved') return true
    if (result.kind === 'conflict') setConflict(true)
    else if (result.kind === 'refused')
      setRefusal({
        message: result.message,
        code: result.code ?? null,
        existing: result.existing ?? null,
      })
    else setRefused('Your dashboard could not be saved just now.')
    return false
  }

  const save = async (
    list: WidgetDraft[],
    head: { name?: string; purpose?: string } = {},
  ): Promise<boolean> => {
    setRefused(null)
    return settle(await saveMutation.mutateAsync({ etag: body.etag, widgets: list, ...head }))
  }

  /** A change that can be undone: save it, and offer the list it replaced. */
  const change = async (list: WidgetDraft[], message: string) => {
    const before = drafts()
    if (await save(list)) {
      toast.info(message, {
        label: 'Undo',
        onClick: () => {
          // Conditional too: on the version the change left, from the cache.
          void saveMutation.mutateAsync({ etag: latestEtag(), widgets: before }).then((result) => {
            if (result.kind === 'conflict') setConflict(true)
          })
        },
      })
    }
  }

  /** Put back a kept version; the state it leaves is kept, so Undo is a restore too. */
  const restore = async (index: number, message: string) => {
    setRefused(null)
    const result = await restoreMutation.mutateAsync({ etag: body.etag, index })
    if (!settle(result)) return
    toast.info(message, {
      label: 'Undo',
      onClick: () => {
        void restoreMutation.mutateAsync({ etag: latestEtag(), index: 0 }).then((again) => {
          if (again.kind === 'conflict') setConflict(true)
        })
      },
    })
  }

  // A CHAT CHANGE EDITED HERE BEFORE IT IS SAVED (owner, 2026-10-20): the
  // confirm card hands its widgets over and opens this dashboard; each opens
  // in the editor in turn, is saved here over HTTP, and once the last one is
  // saved the chat's proposal is set aside so it can never land as well.
  // Closing the editor leaves the proposal pending in the chat.
  const handoff = useStudioHandoff((s) => s.handoff)
  const nextHanded = useStudioHandoff((s) => s.next)
  const clearHanded = useStudioHandoff((s) => s.clear)
  const markSaved = useStudioHandoff((s) => s.markSaved)
  const handed = handoff?.dashboardId === boardId ? handoff : null
  const handedDraft = handed?.drafts[0]
  useEffect(() => {
    if (!handedDraft) return
    // The store's change arrives after render; opening the editor on it is the point.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- opening the handed-over draft
    setAdding(false)
    setRefused(null)
    // A widget the dashboard has is edited in place; any other is added.
    const at = handedDraft.id ? widgets.findIndex((w) => w.id === handedDraft.id) : -1
    setEditing({ at: at >= 0 ? at : 'new', draft: handedDraft, handed: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per handed draft
  }, [handedDraft])

  const commitEdit = async (draft: WidgetDraft) => {
    if (!editing) return
    const list = drafts()
    if (editing.at === 'new') list.push(draft)
    else list[editing.at] = { ...draft, id: list[editing.at]?.id }
    if (!(await save(list))) return
    setEditing(null)
    if (editing.handed && handed) {
      const last = handed.drafts.length <= 1
      nextHanded()
      if (last && handed.proposalId) {
        void cancelProposal(handed.proposalId)
        markSaved(handed.proposalId)
        toast.info('Saved here. The change offered in the chat was set aside.')
      }
    }
  }

  const remove = (at: number) => {
    const list = drafts()
    const [gone] = list.splice(at, 1)
    if (gone) void change(list, `Removed “${gone.title}”.`)
  }

  const move = (at: number, to: 'up' | 'down' | 'top') => {
    const list = drafts()
    const target = to === 'top' ? 0 : to === 'up' ? at - 1 : at + 1
    if (target < 0 || target >= list.length || target === at) return
    const [item] = list.splice(at, 1)
    if (!item) return
    list.splice(target, 0, item)
    void change(list, `Moved “${item.title}”.`)
  }

  const applyFixTo = (widget: Drawn, fix: Fix) => {
    const at = widgets.indexOf(widget)
    const list = drafts()
    const current = list[at]
    if (!current) return
    list[at] = { ...applyFix(current, fix), id: current.id }
    void change(list, `Changed “${widget.title}”.`)
  }

  /** A widget to another dashboard, by the engine's move: it lands there at the top. */
  const moveTo = async (widget: Drawn, board: BoardSummary) => {
    if (!widget.id) return
    setRefused(null)
    const result = await moveMutation.mutateAsync({
      widgetId: widget.id,
      etag: body.etag,
      toId: board.id,
    })
    if (settle(result)) {
      toast.info(`Moved “${widget.title}” to “${board.name}”.`, {
        label: 'Open it',
        onClick: () => {
          void navigate({ to: '/dashboards/$dashboardId', params: { dashboardId: board.id } })
        },
      })
    }
  }

  const add = async (draft: WidgetDraft) => {
    if (await save([...drafts(), draft])) setAdding(false)
  }

  // A preset over a window carries its window, order and scope; one about a
  // company carries the company the reader picked in place of "{symbol}".
  const addPreset = (preset: Preset, symbol?: string) => {
    if (preset.needsSymbol && !symbol) return
    void add(presetWidget(preset, symbol))
  }

  const startEdit = (at: number | 'new', draft: WidgetDraft) => {
    setAdding(false)
    setRefused(null)
    setEditing({ at, draft })
  }

  const actionsFor = (widget: Drawn) => {
    const at = widgets.indexOf(widget)
    return (
      <ActionsMenu
        title={widget.title}
        disabled={saving}
        onEdit={() => {
          startEdit(at, draftOf(widget))
        }}
        onMove={(to) => {
          move(at, to)
        }}
        onRemove={() => {
          remove(at)
        }}
        others={widget.id ? body.boards.filter((b) => b.id !== boardId) : []}
        onMoveTo={(board) => {
          void moveTo(widget, board)
        }}
      />
    )
  }

  const full = widgets.length >= MAX_WIDGETS
  const sections = sectionsOf(body)
  const headings = sections.map((s) => s.title).filter((t): t is string => t !== null)

  // A row of their own under the name: beside it they truncated the name.
  const actions = (
    <div data-role="board-actions" className="flex flex-wrap items-center gap-2">
      <Menu
        label="Versions"
        trigger={(props) => (
          <Button {...props} variant="secondary" size="sm" disabled={body.history.length === 0}>
            <History className="size-4" aria-hidden="true" />
            Versions
          </Button>
        )}
      >
        {(close) =>
          body.history.map((version) => (
            <MenuActionItem
              key={version.index}
              label={`${version.by === 'mitra' ? 'Mitra · ' : ''}${version.label || 'An earlier version'} · ${whenOf(version.at)}`}
              disabled={saving}
              tabIndex={-1}
              onClick={() => {
                close()
                void restore(version.index, 'Put back as it was.')
              }}
            />
          ))
        }
      </Menu>
      <Button
        variant="primary"
        size="sm"
        disabled={saving || full || !vocabulary}
        onClick={() => {
          setEditing(null)
          setRefused(null)
          setAdding(true)
        }}
      >
        Add a measure
      </Button>
      <Menu
        label={`Actions for ${name}`}
        trigger={(props) => (
          <IconButton {...props} aria-label={`More actions for ${name}`} className="size-9">
            <MoreVertical className="size-4" aria-hidden="true" />
          </IconButton>
        )}
      >
        {(close) => (
          <>
            <MenuActionItem
              label="Rename"
              tabIndex={-1}
              onClick={() => {
                close()
                setRenaming(true)
              }}
            />
            <MenuActionItem
              label="Delete dashboard"
              danger
              tabIndex={-1}
              onClick={() => {
                close()
                setDeleting(true)
              }}
            />
          </>
        )}
      </Menu>
    </div>
  )

  return (
    <ScreenLayout
      header={
        <div className="flex min-w-0 flex-col gap-3">
          <TitleRow
            back="/dashboards"
            title={name}
            {...(shownPurpose
              ? {
                  subtitle: (
                    <span data-role="board-purpose" title={shownPurpose} className="line-clamp-1">
                      {shownPurpose}
                    </span>
                  ),
                }
              : {})}
            meta={
              <span className="inline-flex items-center gap-1">
                {body.dashboard?.origin === 'mitra' ? <MitraDot origin="mitra" /> : null}
                <IconButton
                  aria-label={`Rename ${name}`}
                  className="size-8"
                  onClick={() => {
                    setRenaming(true)
                  }}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                </IconButton>
              </span>
            }
          />
          {actions}
        </div>
      }
      pageTitle={name}
    >
      <div data-role="dashboard" className="flex flex-col gap-4">
        {renaming ? (
          <RenameForm
            name={name}
            purpose={purpose}
            saving={saving}
            boards={body.boards}
            boardId={boardId}
            refusal={refusal}
            onCancel={() => {
              setRenaming(false)
            }}
            onSave={(nextName, nextPurpose) => {
              void save(drafts(), { name: nextName, purpose: nextPurpose }).then((ok) => {
                if (ok) setRenaming(false)
              })
            }}
          />
        ) : null}

        {newest?.by === 'mitra' ? (
          <div
            data-role="mitra-change"
            className="flex flex-wrap items-center gap-2 rounded-md bg-tone-info-soft px-4 py-2 text-body-s text-foreground"
          >
            <MitraDot origin="mitra" />
            <span className="min-w-0 flex-1">
              {newest.label || 'Mitra changed this dashboard.'}
            </span>
            <Button
              size="sm"
              variant="secondary"
              disabled={saving}
              onClick={() => {
                void restore(0, 'Mitra’s change taken back.')
              }}
            >
              <Undo2 className="size-4" aria-hidden="true" />
              Undo Mitra’s change
            </Button>
          </div>
        ) : null}

        {body.unavailable.length > 0 ? (
          <p className="text-body-s text-muted-foreground">
            Not available just now: {body.unavailable.join(', ')}.
          </p>
        ) : null}

        {conflict ? (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-2 rounded-md bg-warning-soft px-4 py-3 text-body-s text-foreground"
          >
            <span className="flex-1">
              Your dashboard changed in another tab. Reload it to see the latest.
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setConflict(false)
                setEditing(null)
                onReload()
              }}
            >
              Reload
            </Button>
          </div>
        ) : null}
        {refused && !editing && !adding && !(renaming && refusal?.code === 'name_taken') ? (
          <div role="alert" className="flex flex-wrap items-center gap-2">
            <p className="text-body-s text-destructive">{refused}</p>
            {refusal?.existing ? <OpenExisting id={refusal.existing} /> : null}
          </div>
        ) : null}

        {empty ? (
          <div data-role="dash-empty" className="flex flex-col gap-4">
            {body.holdings.length === 0 ? (
              <EmptyState
                title="Nothing to measure yet"
                description="You hold nothing yet. Add a measure over a watchlist or the market instead."
              />
            ) : vocabulary ? (
              <Starters vocabulary={vocabulary} disabled={saving} onAdd={addPreset} />
            ) : null}
            {onAskMitra ? (
              <AskMitra
                onAsk={(question) => {
                  onAskMitra(addToDashboard(name, question))
                }}
              />
            ) : null}
          </div>
        ) : null}

        {sections.map((section) => (
          <Section
            key={section.title ?? ''}
            title={section.title}
            widgets={section.widgets}
            scopes={body.scopes}
            holdings={body.holdings}
            actionsFor={actionsFor}
            onApplyFix={applyFixTo}
            time={vocabulary?.time ?? null}
          />
        ))}

        {/* The measures lead; ways to add more come after them. */}
        {!empty && vocabulary ? (
          <Suggestions
            vocabulary={vocabulary}
            have={widgets.flatMap((w) => [w.formula, w.title])}
            disabled={saving}
            onAdd={addPreset}
          />
        ) : null}

        {!empty && onAskMitra ? (
          // The chat, from the dashboard itself: add a measure, change one, or ask why a figure is
          // what it is. The dashboard's name rides with the question so Mitra works on this one.
          <AskMitra
            label="Ask Mitra about this dashboard"
            placeholder="For example: add my sector weights"
            onAsk={(question) => {
              onAskMitra(inDashboard(name, question))
            }}
          />
        ) : null}

        {adding && vocabulary ? (
          <MeasureBuilder
            open
            vocabulary={vocabulary}
            sections={headings}
            saving={saving}
            refused={refused}
            full={full}
            onAdd={(draft) => {
              void add(draft)
            }}
            onAddPreset={addPreset}
            onWriteFormula={(draft) => {
              startEdit('new', draft)
            }}
            onAsk={onAskMitra}
            onClose={() => {
              setAdding(false)
              setRefused(null)
            }}
          />
        ) : null}

        {editing ? (
          <WidgetEditor
            key={`${String(editing.at)}:${editing.draft.title}:${editing.draft.formula}`}
            open
            initial={editing.draft}
            isNew={editing.at === 'new'}
            vocabulary={vocabulary}
            saving={saving}
            refused={refused}
            onSave={(draft) => {
              void commitEdit(draft)
            }}
            onAsk={onAskMitra}
            onClose={() => {
              if (editing.handed) clearHanded()
              setEditing(null)
              setRefused(null)
            }}
          />
        ) : null}

        <Modal
          open={deleting}
          onOpenChange={setDeleting}
          title={`Delete “${name}”?`}
          description="Its measures go with it. Your holdings and watchlists are not touched."
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setDeleting(false)
                }}
              >
                Keep it
              </Button>
              <Button
                variant="destructive"
                disabled={removeMutation.isPending}
                onClick={() => {
                  void removeMutation
                    .mutateAsync({ id: boardId, etag: body.etag })
                    .then((result) => {
                      if (result.kind === 'removed') {
                        setDeleting(false)
                        toast.info(`Deleted “${name}”.`)
                        void navigate({ to: '/dashboards' })
                      } else if (result.kind === 'conflict') {
                        setDeleting(false)
                        setConflict(true)
                      } else {
                        setDeleting(false)
                        setRefused('Your dashboard could not be deleted just now.')
                      }
                    })
                }}
              >
                Delete
              </Button>
            </div>
          }
        />
      </div>
    </ScreenLayout>
  )
}
